import { ReactNode, useRef, MouseEvent, useCallback, useMemo, FC, memo } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  AlertDialogProps,
  useBreakpointValue,
  Heading,
  Text,
  Flex,
} from "@chakra-ui/react";

import { Button } from "../Button";
import { Timer } from "../Timer";
import { getGapStyles } from "../../../theme/utils";
import { HeadingVariants } from "../../../theme/variants";

interface DialogProps extends Omit<AlertDialogProps, "leastDestructiveRef"> {
  title: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: (event?: MouseEvent<HTMLButtonElement>) => void;
  withoutButtons?: boolean;
  withoutCancel?: boolean;
  disabled?: boolean;
  footer?: ReactNode;
  secondsTimer?: number;
  children: ReactNode;
  id: string;
}

export const Dialog: FC<DialogProps> = memo(
  ({
    isOpen,
    onClose,
    title,
    onConfirm,
    children = null,
    withoutButtons,
    withoutCancel,
    disabled,
    cancelText = "Cancel",
    confirmText = "Yes",
    footer,
    secondsTimer,
    id,
    ...props
  }) => {
    const cancelRef = useRef(null);

    const dialogSize = useBreakpointValue({ base: "sm", md: "md", lg: "lg" });
    const titleVariant = useBreakpointValue({ base: HeadingVariants.smallMd, lg: HeadingVariants.smallLg });
    const bodyVariant = useBreakpointValue({ base: "md", lg: "lg" });
    const buttonSize = useBreakpointValue({ base: "md", lg: "lg" });
    const buttonProps = useMemo(
      () => ({
        size: buttonSize,
        flex: "1 1 1px",
      }),
      [buttonSize]
    );

    const handleConfirmClick = useCallback(
      (event?: MouseEvent<HTMLButtonElement>) => {
        if (onConfirm) {
          onConfirm(event);
        }

        onClose();
      },
      [onConfirm, onClose]
    );

    return (
      <AlertDialog
        isCentered
        size={dialogSize}
        isOpen={isOpen}
        onClose={onClose}
        leastDestructiveRef={cancelRef}
        id={id}
        {...props}
      >
        <AlertDialogOverlay bg="blackAlpha.800" />

        <AlertDialogContent m={4} boxShadow="modal" borderRadius="lg">
          <AlertDialogHeader color="sapphireBlue.600" pt={6} pb={children ? undefined : 6}>
            <Heading as="div" variant={titleVariant}>
              {title}
            </Heading>
          </AlertDialogHeader>
          <AlertDialogCloseButton className={`${id}-close-button`} borderRadius="full" />

          {children && (
            <AlertDialogBody as={Flex} color="sapphireBlue" pb={withoutButtons ? 6 : undefined}>
              <Text as="div" flex="1 1 100%" variant={bodyVariant}>
                {children}
              </Text>
            </AlertDialogBody>
          )}

          {!withoutButtons && (
            <AlertDialogFooter {...getGapStyles(2)} pb={6}>
              {footer || (
                <>
                  {(!onConfirm || withoutCancel) && <Text h="1" flex="1 1 1px" />}
                  {!withoutCancel && (
                    <Button ref={cancelRef} onClick={onClose} variant="outline" {...buttonProps}>
                      {cancelText}
                    </Button>
                  )}
                  {onConfirm && (
                    <Button
                      className={`${id}-confirm-button`}
                      onClick={handleConfirmClick}
                      {...buttonProps}
                      disabled={disabled}
                    >
                      {confirmText}
                      {secondsTimer && (
                        <>
                          <Text w="1" />
                          <Timer seconds={secondsTimer} small />
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
    );
  }
);
